import type {
  OptimizerResult,
} from "@/types/feeds";
import type { SelectionSide } from "@/types/evbook";
import { isOverSlotSide, isUnderSlotSide } from "@/lib/selection-side";
import { normalizeFantasySportsbookId } from "@/types/feeds";
import { formatGameStartTimeUtcToEST } from "./formatting";
import type { DisplayPlayerProp, PlatformType } from "./types";
import {
  isDevEnvironment,
  selectFantasyBookId,
  selectFantasyOdds,
  toFiniteNumber,
} from "./transform-helpers";
import { applyActiveSportsbookOdds, type OddsSlotContext } from "./book-odds-assignment";
import { applyComparisonMetadata } from "./comparison-metadata";
import { applySlipEdgeMetrics } from "./slip-edge-metrics";

export function transformOptimizerResults(
  results: OptimizerResult[],
  activeSportsbook: string,
  options?: {
    platform?: PlatformType;
    slipTypeId?: string | null;
    boostPct?: number;
    standardDecimal?: number | null;
    league?: string | null;
  },
): DisplayPlayerProp[] {
  if (!results || results.length === 0) return [];

  const selected =
    normalizeFantasySportsbookId(activeSportsbook || "prizepicks") ||
    "prizepicks";

  const filteredResults = results.filter((result) => {
    const book = selectFantasyBookId(result);
    return book === selected;
  });

  return filteredResults.map((result: OptimizerResult) => {
    const rowSportsbook = selectFantasyBookId(result);
    const rawSide =
      (result as any).selection_side ??
      (result as any).selectionSide ??
      result.over_under ??
      "";
    const normalizedSide = String(rawSide).toUpperCase();
    const rowSide: SelectionSide =
      normalizedSide === "OVER" ||
      normalizedSide === "UNDER" ||
      normalizedSide === "HOME" ||
      normalizedSide === "AWAY" ||
      normalizedSide === "YES" ||
      normalizedSide === "NO"
        ? (normalizedSide as SelectionSide)
        : "OVER";
    const isOverSelection = isOverSlotSide(rowSide);
    const isUnderSelection = isUnderSlotSide(rowSide);
    const isOverUnderSelection = isOverSelection || isUnderSelection;
    const selectionLabel =
      result.selection ??
      (result.selection_side ?? result.selectionSide) ??
      rowSide;
    const eventStartIso = result.event_start ?? result.game_start_time ?? null;
    const formattedGameStart =
      eventStartIso && typeof eventStartIso === "string"
        ? formatGameStartTimeUtcToEST(eventStartIso)
        : "";
    const eventName =
      result.event ??
      (result.team && result.opponent
        ? `${result.team} vs ${result.opponent}`
        : result.team ?? result.player_name);
    const leagueLabel = result.league ?? result.sport ?? null;
    const marketLabel = result.market_display ?? result.market ?? result.stat_type;
    const fantasyAmerican = selectFantasyOdds(result);
    let fantasyOppositeAmerican = toFiniteNumber(result.opp_american_odds);
    if (fantasyOppositeAmerican == null && result.sportsbook_odds) {
      const primaryBookOdds =
        result.sportsbook_odds[rowSportsbook] ??
        Object.entries(result.sportsbook_odds).find(
          ([book]) => normalizeFantasySportsbookId(book) === rowSportsbook
        )?.[1];
      if (primaryBookOdds) {
        fantasyOppositeAmerican = isOverSelection
          ? toFiniteNumber(primaryBookOdds.under)
          : toFiniteNumber(primaryBookOdds.over);
      }
    }
    const fairProbability = toFiniteNumber(result.fair_implied_probability);
    const legacyPct = toFiniteNumber(result.odds_to_hit_percentage);
    const consensusProbability = toFiniteNumber(
      result.consensus_implied_probability,
    );
    const evRoiPercent =
      typeof result.ev_roi === "number"
        ? result.ev_roi * 100
        : typeof result.ev_percentage === "number"
          ? result.ev_percentage
          : null;
    const oddsToHitPercentage =
      consensusProbability != null
        ? consensusProbability * 100
        : fairProbability != null
          ? fairProbability * 100
          : legacyPct;

    const prop: DisplayPlayerProp = {
      id: result.id || `${result.player_id}-${result.stat_type}-${result.line}`,
      game_id: result.game_id,
      betting_market_id: 0,
      player_id: result.player_id,
      player_name: result.player_name,
      sport: result.sport,
      league: leagueLabel ?? null,
      team: result.team || "",
      opponent: result.opponent || null,
      game_start_time: formattedGameStart,
      game_start_timestamp: eventStartIso,
      event_name: eventName ?? null,
      event_short: result.event_short ?? null,
      team_short: result.team_short ?? null,
      opponent_short: result.opponent_short ?? null,
      player_position: result.player_position ?? null,
      player_image_url: result.player_image_url ?? null,
      sportsbook_group:
        result.fantasy_sportsbook_group ?? result.sportsbook_group ?? null,
      stat_type: result.stat_type,
      line: result.line,
      market: result.market ?? null,
      market_type: result.market_type ?? null,
      market_display: marketLabel,
      bet_type: rowSide,
      selection_label: selectionLabel ?? null,
      selection_side: result.selection_side ?? rowSide,
      event_id: result.event_id ?? null,
      entity_key: result.entity_key ?? null,
      selection_id: result.selection_id ?? null,
      opp_selection_id: result.opp_selection_id ?? null,
      primary_book: rowSportsbook,
      primary_odds: fantasyAmerican,
      is_available: true,
      odds_to_hit: oddsToHitPercentage ?? 0,
      under_odds_to_hit: 0,
      created_at: result.created_at || "",
      updated_at: result.updated_at || "",
      ev_percentage: evRoiPercent ?? 0,
      ev_roi: evRoiPercent ?? null,
      consensus_implied_probability: consensusProbability ?? null,
      peer_book_count: result.peer_book_count ?? null,
      pairing_key: result.pairing_key ?? null,
      opposing_book: result.opposing_book ?? null,
      __comparison_odds_raw: result.comparison_odds,

      prizepicks_odds: null,
      prizepicks_over_odds: null,
      prizepicks_under_odds: null,
      underdog_odds: null,
      underdog_over_odds: null,
      underdog_under_odds: null,
      underdog_direct_odds: null,
      sleeper_odds: null,
      sleeper_over_odds: null,
      sleeper_under_odds: null,
      sleeper_direct_odds: null,
      fliff_odds: null,
      draftkings_odds: null,
      fanduel_odds: null,
      caesars_odds: null,
      betmgm_odds: null,
      fanatics_odds: null,
      hardrock_odds: null,
      betr_odds: null,
      novig_odds: null,
      prophetx_odds: null,
      dabble_odds: null,
      circa_odds: null,
      pinnacle_odds: null,
      kalshi_odds: null,
      draftkings_over_odds: null,
      draftkings_under_odds: null,
      fanduel_over_odds: null,
      fanduel_under_odds: null,
      caesars_over_odds: null,
      caesars_under_odds: null,
      betmgm_over_odds: null,
      betmgm_under_odds: null,
      fanatics_over_odds: null,
      fanatics_under_odds: null,
      hardrock_over_odds: null,
      hardrock_under_odds: null,
      betr_over_odds: null,
      betr_under_odds: null,
      novig_over_odds: null,
      novig_under_odds: null,
      prophetx_over_odds: null,
      prophetx_under_odds: null,
      dabble_over_odds: null,
      dabble_under_odds: null,
      circa_over_odds: null,
      circa_under_odds: null,
      pinnacle_over_odds: null,
      pinnacle_under_odds: null,
      kalshi_over_odds: null,
      kalshi_under_odds: null,
      polymarket_odds: null,
      polymarket_over_odds: null,
      polymarket_under_odds: null,
      rebet_odds: null,
      rebet_over_odds: null,
      rebet_under_odds: null,
      draftkingspredictions_odds: null,
      draftkingspredictions_over_odds: null,
      draftkingspredictions_under_odds: null,
      kambi_odds: null,
      kambi_over_odds: null,
      kambi_under_odds: null,
      betonline_odds: null,
      betonline_over_odds: null,
      betonline_under_odds: null,
      propbuilder_odds: null,
      propbuilder_over_odds: null,
      propbuilder_under_odds: null,
      bovada_odds: null,
      bovada_over_odds: null,
      bovada_under_odds: null,
      betrsportsbook_odds: null,
      betrsportsbook_over_odds: null,
      betrsportsbook_under_odds: null,
      shuffle_odds: null,
      shuffle_over_odds: null,
      shuffle_under_odds: null,
      sportzino_odds: null,
      sportzino_over_odds: null,
      sportzino_under_odds: null,
      sportsbettingag_odds: null,
      sportsbettingag_over_odds: null,
      sportsbettingag_under_odds: null,
      dkpick6_odds: null,
      dkpick6_over_odds: null,
      dkpick6_under_odds: null,
      parlayplay_odds: null,
      parlayplay_over_odds: null,
      parlayplay_under_odds: null,
      boomsports_odds: null,
      boomsports_over_odds: null,
      boomsports_under_odds: null,
      chalkboardpicks_odds: null,
      chalkboardpicks_over_odds: null,
      chalkboardpicks_under_odds: null,
      chalkboardssp_odds: null,
      chalkboardssp_over_odds: null,
      chalkboardssp_under_odds: null,
      smacktok_odds: null,
      smacktok_over_odds: null,
      smacktok_under_odds: null,
      fourcx_odds: null,
      fourcx_over_odds: null,
      fourcx_under_odds: null,
      fourcasters_odds: null,
      fourcasters_over_odds: null,
      fourcasters_under_odds: null,
      sxbet_odds: null,
      sxbet_over_odds: null,
      sxbet_under_odds: null,
      bet105_odds: null,
      bet105_over_odds: null,
      bet105_under_odds: null,
      onyxodds_odds: null,
      onyxodds_over_odds: null,
      onyxodds_under_odds: null,
      thunderpick_odds: null,
      thunderpick_over_odds: null,
      thunderpick_under_odds: null,
      cryptocom_odds: null,
      cryptocom_over_odds: null,
      cryptocom_under_odds: null,
      bet_link: result.bet_link ?? null,
    };

    const primaryLine =
      typeof result.line === "number"
        ? result.line
        : toFiniteNumber((result as any).line);

    if (isDevEnvironment && primaryLine == null) {
      // eslint-disable-next-line no-console
      console.warn(
        "[transformOptimizerResults] primaryLine is null for row; cross-line comparison will be skipped",
        { player_name: result.player_name, stat_type: result.stat_type },
      );
    }

    const oddsSlotContext: OddsSlotContext = {
      isOverUnderSelection,
      isOverSelection,
      isUnderSelection,
    };

    applyActiveSportsbookOdds({
      prop,
      result,
      rowSportsbook,
      fantasyAmerican,
      fantasyOppositeAmerican,
      isOverSelection,
      isUnderSelection,
    });

    applyComparisonMetadata({
      prop,
      result,
      rowSportsbook,
      rowSide,
      primaryLine,
      oddsSlotContext,
    });

    applySlipEdgeMetrics(prop, rowSportsbook, options);


    return prop;
  });
}
